import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, AlertCircle, Calendar, User, ArrowRight, Award, BookOpen, FileText, Heart, XCircle, Edit3 } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { formatDate, formatDateTime, getBeijingToday } from '../utils/timeUtils';
import { typeNames } from '../constants/questionTypes';
import AvatarSelectorModal from '../components/AvatarSelectorModal';

interface Assignment {
  id: string;
  assignmentId: string;
  paperId: string;
  paperTitle: string;
  teacherName: string;
  status: 'pending' | 'completed' | 'expired';
  score: number | null;
  objectiveScore: number | null;
  programmingScore: number | null;
  programmingGradingStatus: string | null;
  enabledQuestionTypes: string[] | null;
  deadline: string | null;
  createdAt: string;
  completedAt: string | null;
  examRecordId: string | null;
  assignmentType: 'class' | 'competition';
}

export default function AssignmentsPage() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<{
    pending: Assignment[];
    completed: Assignment[];
    expired: Assignment[];
  }>({ pending: [], completed: [], expired: [] });
  const [loading, setLoading] = useState(true);
  const [assignmentType, setAssignmentType] = useState<'class' | 'competition' | 'all'>('all');
  const [user, setUser] = useState<any>(null);
  const [hasValidPackage, setHasValidPackage] = useState(false);
  const [memberType, setMemberType] = useState<string>('');
  const [expPoints, setExpPoints] = useState<number>(0);
  const [avatarSeed, setAvatarSeed] = useState<string>('');
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  useEffect(() => {
    loadAssignments();
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session?.user) return;

      const userId = session.user.id;

      // 获取用户信息
      const { data: accountData } = await supabase
        .from('user_accounts')
        .select('role, mobile, username')
        .eq('auth_user_id', userId)
        .maybeSingle();

      // 获取用户头像种子
      const { data: profileData } = await supabase
        .from('profiles')
        .select('avatar_seed')
        .eq('id', userId)
        .maybeSingle();

      if (profileData?.avatar_seed) {
        setAvatarSeed(profileData.avatar_seed);
      }

      if (accountData) {
        setUser({ id: userId, phone: accountData.mobile || '', role: accountData.role || 'student', username: accountData.username || '' });
      }

      // 检查是否有有效套餐
      const now = getBeijingToday();
      const { data: packages } = await supabase
        .from('user_packages')
        .select('id, duration_type')
        .eq('user_id', userId)
        .eq('is_active', true)
        .gte('expire_date', now)
        .limit(1);

      const hasPackage = packages && packages.length > 0;
      setHasValidPackage(hasPackage);

      // 设置会员类型
      if (hasPackage) {
        const durationType = packages[0].duration_type;
        const typeMap: Record<string, string> = {
          'month': '月会员',
          'quarter': '季会员',
          'halfYear': '半年会员',
          'year': '年会员'
        };
        setMemberType(durationType ? (typeMap[durationType] || '会员') : '会员');
      } else {
        setMemberType('普通用户');
      }

      // 查询用户U币余额
      const { data: pointAccountData } = await supabase
        .from('user_point_accounts')
        .select('total_points')
        .eq('user_id', userId)
        .eq('role', 'student')
        .maybeSingle();

      if (pointAccountData) {
        setExpPoints(pointAccountData.total_points || 0);
      }
    } catch (error) {
      console.error('加载用户信息失败:', error);
    }
  };

  // 处理头像选择
  const handleAvatarSelect = async (seed: string, style: string) => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session?.user) return;

      const userId = session.user.id;

      // 更新 profiles 表中的 avatar_seed
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_seed: seed })
        .eq('id', userId);

      if (error) {
        console.error('保存头像失败:', error);
        alert('保存头像失败，请稍后重试');
        return;
      }

      setAvatarSeed(seed);
    } catch (error) {
      console.error('保存头像失败:', error);
      alert('保存头像失败，请稍后重试');
    }
  };

  // 获取头像URL
  const getAvatarUrl = () => {
    if (avatarSeed) {
      // 解析种子格式：style:seed
      const parts = avatarSeed.split(':');
      if (parts.length === 2) {
        const [style, seed] = parts;
        // 根据风格设置背景色
        const bgColors: Record<string, string> = {
          bottts: 'e0e7ff',
          avataaars: 'ffe4e6',
          'fun-emoji': 'fef3c7',
          adventurer: 'dbeafe',
          micah: 'd1fae5',
          'big-smile': 'fce7f3',
          personas: 'e0e7ff',
          lorelei: 'ede9fe',
          notionists: 'f0fdf4',
          'open-peeps': 'fff7ed',
          'pixel-art': 'ecfccb',
          shapes: 'cffafe',
        };
        const bgColor = bgColors[style] || 'e0e7ff';
        return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}&backgroundColor=${bgColor}`;
      }
    }
    // 默认使用用户ID作为种子
    return `https://api.dicebear.com/7.x/bottts/svg?seed=${user?.id || user?.phone || 'user'}&backgroundColor=e0e7ff`;
  };

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'get_student_assignments' }),
      });

      const result = await response.json();
      if (result.success) {
        // Edge Function 返回 { all, pending, completed, expired }
        // 前端 state 只需要 { pending, completed, expired }
        setAssignments({
          pending: result.data.pending || [],
          completed: result.data.completed || [],
          expired: result.data.expired || []
        });
      }
    } catch (error) {
      console.error('加载作业列表失败:', error);
    }
    setLoading(false);
  };

  const handleStartAssignment = async (assignment: Assignment) => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'start_assignment',
          assignmentId: assignment.id,
        }),
      });

      const result = await response.json();
      if (result.success) {
        // 跳转到答题页面，带上作业ID参数
        navigate(`/quiz/${assignment.paperId}?assignmentId=${assignment.id}`);
      } else {
        alert(result.message || '开始作业失败');
      }
    } catch (error) {
      console.error('开始作业失败:', error);
      alert('开始作业失败，请稍后重试');
    }
  };

  const getDeadlineStatus = (deadline: string | null) => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diffHours = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60));

    if (diffHours < 0) return { text: '已过期', color: 'text-red-600', bg: 'bg-red-50' };
    if (diffHours <= 24) return { text: `剩余${diffHours}小时`, color: 'text-orange-600', bg: 'bg-orange-50' };
    const diffDays = Math.ceil(diffHours / 24);
    return { text: `剩余${diffDays}天`, color: 'text-blue-600', bg: 'bg-blue-50' };
  };

  // 查看作业详情 - 与答题记录详情保持一致
  const handleViewDetail = async (assignment: Assignment) => {
    if (!assignment.examRecordId) return;

    try {
      // 获取考试记录详情
      const { data: record, error } = await supabase
        .from('exam_records')
        .select('*')
        .eq('id', assignment.examRecordId)
        .single();

      if (error || !record) {
        console.error('获取考试记录失败:', error);
        return;
      }

      // 跳转到 Quiz 页面回顾模式，与 ExamRecordsPage 保持一致
      navigate(`/quiz/${record.paper_id}`, {
        state: {
          fromResult: true,
          resultData: {
            paperId: record.paper_id,
            paperTitle: record.paper_title,
            score: record.score,
            totalScore: record.total_score,
            timeLeft: record.time_left,
            examDuration: record.exam_duration,
            answers: record.answers || {},
            questions: record.questions || [],
            questionFeedbacks: record.question_feedbacks || null
          }
        }
      });
    } catch (error) {
      console.error('查看详情失败:', error);
    }
  };

  const renderAssignmentCard = (assignment: Assignment, isPending: boolean) => {
    const deadlineStatus = getDeadlineStatus(assignment.deadline);
    
    return (
      <motion.div
        key={assignment.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
      >
        {/* 第一行：试卷标题 + 得分/按钮 */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {/* 作业类型标识 */}
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              assignment.assignmentType === 'competition'
                ? 'bg-purple-100 text-purple-700 border border-purple-200'
                : 'bg-blue-100 text-blue-700 border border-blue-200'
            }`}>
              {assignment.assignmentType === 'competition' ? '赛考' : '课堂'}
            </span>
            <h3 className="font-semibold text-gray-800">{assignment.paperTitle}</h3>
            {assignment.enabledQuestionTypes && assignment.enabledQuestionTypes.length > 0 && (
              <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full border border-amber-200">
                {assignment.enabledQuestionTypes.map(t => typeNames[t] || t).join('、')}
              </span>
            )}
          </div>

          {isPending ? (
            <button
              onClick={() => handleStartAssignment(assignment)}
              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              开始答题
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : assignment.status === 'completed' ? (
            (() => {
              // 分项得分 — 直接使用数据库保存的值，与 QuizReviewMode.tsx 显示逻辑一致
              const hasProgrammingQuestions = assignment.programmingScore !== null || assignment.programmingGradingStatus === 'pending';
              const programmingGraded = assignment.programmingScore !== null;

              const displayObjectiveScore = assignment.objectiveScore ?? 0;
              const displayProgrammingScore: number | string = programmingGraded ? (assignment.programmingScore as number) : '待批阅';
              const displayTotalScore: number | string = (hasProgrammingQuestions && !programmingGraded) ? '-' : (assignment.score ?? 0);

              // 判断是否有客观题（objectiveScore 不为 null 表示有客观题）
              const hasObjectiveQuestions = assignment.objectiveScore !== null;

              return (
                <div className="flex items-center gap-2 text-sm whitespace-nowrap">
                  {/* 客观题得分 - 蓝色标签（仅当有客观题时显示） */}
                  {hasObjectiveQuestions && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-lg border bg-blue-50 border-blue-200">
                      <span className="text-blue-700 text-xs">客观题：</span>
                      <span className="text-blue-700 font-bold text-sm">{displayObjectiveScore}分</span>
                    </div>
                  )}

                  {/* 编程题得分 - 根据批阅状态显示不同颜色 */}
                  {hasProgrammingQuestions && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${
                      displayProgrammingScore === '待批阅'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-purple-50 border-purple-200'
                    }`}>
                      <span className={`text-xs ${
                        displayProgrammingScore === '待批阅'
                          ? 'text-amber-700'
                          : 'text-purple-700'
                      }`}>编程题：</span>
                      <span className={`font-bold text-sm ${
                        displayProgrammingScore === '待批阅'
                          ? 'text-amber-600'
                          : 'text-purple-700'
                      }`}>
                        {displayProgrammingScore === '待批阅' ? '待批阅' : `${displayProgrammingScore}分`}
                      </span>
                    </div>
                  )}

                  {/* 总分 - 根据编程题批阅状态显示 */}
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${
                    displayTotalScore === '-' ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'
                  }`}>
                    <span className={`text-xs ${
                      displayTotalScore === '-' ? 'text-gray-500' : 'text-red-700'
                    }`}>总分：</span>
                    <span className={`font-bold text-sm ${
                      displayTotalScore === '-' ? 'text-gray-400' : 'text-red-600'
                    }`}>
                      {displayTotalScore === '-' ? '-' : `${displayTotalScore}分`}
                    </span>
                  </div>

                  {assignment.examRecordId && (
                    <button
                      onClick={() => handleViewDetail(assignment)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      查看
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-gray-400" />
              <span className="text-gray-500">已过期</span>
            </div>
          )}
        </div>

        {/* 第二行：元数据信息 */}
        <div className="flex items-center gap-4 text-sm text-gray-500 whitespace-nowrap">
          <div className="flex items-center gap-1">
            <User className="w-4 h-4 flex-shrink-0" />
            <span>{assignment.teacherName}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span>发布于 {formatDate(assignment.createdAt)}</span>
          </div>
          {assignment.deadline && (
            <div className="inline-flex items-center gap-1 text-gray-500">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>截止：{formatDateTime(assignment.deadline)}</span>
              {deadlineStatus && deadlineStatus.text !== '已过期' && (
                <span className={`ml-1 text-xs ${deadlineStatus?.color}`}>({deadlineStatus.text})</span>
              )}
            </div>
          )}
          {assignment.status === 'completed' && assignment.completedAt && (
            <div className="inline-flex items-center gap-1 text-gray-500">
              <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <span>完成于 {formatDateTime(assignment.completedAt)}</span>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // 根据作业类型筛选
  const filterByType = (items: Assignment[]) => {
    if (assignmentType === 'all') return items;
    // 使用 assignmentType 字段进行筛选
    return items.filter(item => item.assignmentType === assignmentType);
  };

  // 只显示待完成的作业
  const displayAssignments = filterByType(assignments.pending);

  return (
    <div className="min-h-screen bg-gray-50 pt-4 pb-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* 顶部区域：左侧头像用户名 + 右侧功能按钮 */}
        <div className="bg-white rounded-3xl shadow-xl shadow-indigo-100/60 p-6 mb-6 flex items-stretch gap-6 overflow-hidden relative">
          {/* 装饰背景 */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-50"></div>
          <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full opacity-40"></div>

          {/* 左侧：头像和用户名 */}
          <div className="flex flex-col items-center justify-center min-w-[140px] py-2 relative z-10">
            <div
              className="relative mb-3 cursor-pointer group"
              onClick={() => setIsAvatarModalOpen(true)}
            >
              {/* 头像 */}
              <img
                src={getAvatarUrl()}
                alt="用户头像"
                className={`w-16 h-16 rounded-full border-3 ${hasValidPackage ? 'border-yellow-400' : 'border-slate-200'} shadow-lg transition-transform group-hover:scale-105`}
                style={{ boxShadow: hasValidPackage ? '0 0 0 3px rgba(251,191,36,0.3), 0 4px 12px rgba(99,102,241,0.2)' : '' }}
              />
              {/* 编辑图标覆盖层 */}
              <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Edit3 className="w-5 h-5 text-white drop-shadow-md" />
              </div>
              {/* 皇冠 - 有有效套餐时显示 */}
              {hasValidPackage && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 z-20 animate-bounce-slow pointer-events-none">
                  <svg viewBox="0 0 24 24" fill="none" className="w-full h-full drop-shadow-md">
                    <path d="M2 8L5 17H19L22 8L17 11L12 4L7 11L2 8Z" fill="#FFD700" stroke="#F59E0B" strokeWidth="1.5" strokeLinejoin="round"/>
                    <circle cx="5" cy="17" r="1.5" fill="#EF4444"/>
                    <circle cx="12" cy="17" r="1.5" fill="#3B82F6"/>
                    <circle cx="19" cy="17" r="1.5" fill="#EF4444"/>
                    <circle cx="12" cy="4" r="1.5" fill="#FDE68A"/>
                  </svg>
                </div>
              )}
            </div>
            {/* 用户名（金色） */}
            <span className={`text-lg font-bold ${hasValidPackage ? 'bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent' : 'text-gray-700'}`}>
              {user?.username || (user?.phone?.includes('@') ? user.phone.split('@')[0] : user?.phone) || '用户'}
            </span>
            <span className={`text-xs font-medium mt-1 ${hasValidPackage ? 'text-amber-500' : 'text-gray-400'}`}>{memberType || '普通用户'}</span>
            <div className="flex items-center gap-1 mt-1.5 px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full border border-purple-200 shadow-sm">
              <span className="text-xs font-semibold text-purple-600">U币 {expPoints}</span>
            </div>
          </div>

          {/* 分隔线 */}
          <div className="w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent self-stretch"></div>

          {/* 右侧：三个功能按钮竖向排列 */}
          <div className="flex-1 flex flex-col justify-center gap-3 relative z-10">
            <button
              onClick={() => navigate('/exam-records')}
              className="group flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 hover:translate-x-1 hover:shadow-md hover:shadow-blue-100 transition-all duration-300"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-200 group-hover:scale-110 transition-transform">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm font-semibold text-gray-800">答题记录</span>
                <span className="text-xs text-gray-400">查看历史作答</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-blue-500 transition-colors" />
            </button>

            <button
              onClick={() => navigate('/favorites')}
              className="group flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-100 hover:translate-x-1 hover:shadow-md hover:shadow-orange-100 transition-all duration-300"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-md shadow-orange-200 group-hover:scale-110 transition-transform">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm font-semibold text-gray-800">我的收藏</span>
                <span className="text-xs text-gray-400">收藏的题目</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-orange-500 transition-colors" />
            </button>

            <button
              onClick={() => navigate('/wrong-questions')}
              className="group flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-100 hover:translate-x-1 hover:shadow-md hover:shadow-rose-100 transition-all duration-300"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-md shadow-rose-200 group-hover:scale-110 transition-transform">
                <XCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm font-semibold text-gray-800">我的错题</span>
                <span className="text-xs text-gray-400">错题本复习</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-rose-500 transition-colors" />
            </button>
          </div>
        </div>

        {/* 主要元素：课堂作业和考级竞赛 - 大卡片设计 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* 课堂作业 */}
          <button
            onClick={() => setAssignmentType(assignmentType === 'class' ? 'all' : 'class')}
            className={`relative overflow-hidden flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-300 ${
              assignmentType === 'class'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-200 ring-2 ring-white/50'
                : 'bg-gradient-to-r from-blue-400 to-blue-500 shadow-md shadow-blue-100 hover:shadow-lg hover:shadow-blue-200'
            }`}
          >
            {/* 装饰背景 */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />

            <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white/20 backdrop-blur-sm flex-shrink-0 ${
              assignmentType === 'class' ? 'ring-2 ring-white/40' : ''
            }`}>
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col items-start flex-1 min-w-0">
              <span className="text-lg font-bold text-white drop-shadow-sm">
                课堂作业
              </span>
              <span className="text-xs text-blue-100 font-medium">
                {assignments.pending.filter(a => a.assignmentType === 'class').length} 个待完成
              </span>
            </div>
          </button>

          {/* 考级竞赛 */}
          <button
            onClick={() => setAssignmentType(assignmentType === 'competition' ? 'all' : 'competition')}
            className={`relative overflow-hidden flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-300 ${
              assignmentType === 'competition'
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 shadow-lg shadow-purple-200 ring-2 ring-white/50'
                : 'bg-gradient-to-r from-purple-400 to-purple-500 shadow-md shadow-purple-100 hover:shadow-lg hover:shadow-purple-200'
            }`}
          >
            {/* 装饰背景 */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />

            <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white/20 backdrop-blur-sm flex-shrink-0 ${
              assignmentType === 'competition' ? 'ring-2 ring-white/40' : ''
            }`}>
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col items-start flex-1 min-w-0">
              <span className="text-lg font-bold text-white drop-shadow-sm">
                考级竞赛
              </span>
              <span className="text-xs text-purple-100 font-medium">
                {assignments.pending.filter(a => a.assignmentType === 'competition').length} 个待完成
              </span>
            </div>
          </button>
        </div>

        {/* 作业列表 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {displayAssignments.length > 0 ? (
            <div className="space-y-4">
              {displayAssignments.map((assignment) =>
                renderAssignmentCard(
                  assignment,
                  assignment.status === 'pending'
                )
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-200" />
              <p className="text-gray-500 mb-4">暂无待完成的作业</p>
              <button
                onClick={() => navigate('/exam-records')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4" />
                <span>查看已完成作业</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 头像选择弹窗 */}
      <AvatarSelectorModal
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
        currentSeed={avatarSeed}
        onSelect={handleAvatarSelect}
      />
    </div>
  );
}
